import React from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Progress from "common/Progress/Progress.tsx";
import Text from "common/Typography/Text.tsx";
import { BulkCancelProgressData } from "@shared/types/common/jobs.types";
import Title from "common/Typography/Title.tsx";
import { useTheme } from "styled-components";

export interface BulkProgressStatsProps {
  jobData: BulkCancelProgressData | null;
  total: number;
  title?: string;
  subtitle: string;
  successLabel: string;
  failedLabel: string;
  inactiveLabel?: string;
  alreadyClosedLabel?: string;
}

export const BulkProgressStats: React.FC<BulkProgressStatsProps> = ({
  jobData,
  total,
  title,
  subtitle,
  successLabel,
  failedLabel,
  inactiveLabel,
  alreadyClosedLabel,
}) => {
  const theme = useTheme();
  const success = jobData?.done ?? 0;
  const failed = jobData?.failed ?? 0;
  const inactive = jobData?.inactive_market ?? 0;
  const alreadyClosed = jobData?.already_closed ?? 0;
  const pending = jobData?.pending ?? 0;
  const completed = success + failed + inactive + alreadyClosed;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = !pending && completed > 0 && completed >= total;

  return (
    <>
      <Title level={3}>{title}</Title>
      <Text $size="S" type="secondary" style={{ marginBottom: 4 }}>
        {subtitle}
      </Text>
      <Progress
        percent={percent}
        status={isComplete ? (failed > 0 ? "exception" : "success") : "active"}
        size="small"
      />
      <div
        style={{
          marginTop: 4,
          fontSize: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          maxWidth: 320,
          overflowY: "auto",
          maxHeight: 60,
        }}
      >
        <span>
          <CheckCircleFilled style={{ color: "#52c41a" }} /> {success}{" "}
          {successLabel}
        </span>
        <span>
          <CloseCircleFilled style={{ color: "#ff4d4f" }} /> {failed}{" "}
          {failedLabel}
        </span>
        {inactiveLabel !== undefined && inactive > 0 && (
          <span>
            <ExclamationCircleOutlined style={{ color: theme.colors.orange }} />{" "}
            {inactive} {inactiveLabel}
          </span>
        )}
        {alreadyClosedLabel !== undefined && alreadyClosed > 0 && (
          <span>
            <MinusCircleOutlined style={{ color: "#8c8c8c" }} /> {alreadyClosed}{" "}
            {alreadyClosedLabel}
          </span>
        )}
      </div>
    </>
  );
};

export default BulkProgressStats;
